import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { applyActionCode, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '@/firebase';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { mapAuthError } from '@/utils/authErrorMap';
import LoadingSpinner from '@/shared/components/LoadingSpinner';

/**
 * INSTRUMENTATION (2026-08-29): this handler swallowed every Firebase email-action
 * failure into red UI text with no analytics, so GA4 showed 1 `email_verified`
 * and no way to tell whether the other clicks failed or never arrived. Emits
 * `auth_error` with the same shape as iOS.
 */
const trackAuthError = (method: string, error: unknown, overrides: { type?: string; message?: string } = {}) => {
  const mapped = mapAuthError(error);
  logAnalyticsEvent(ANALYTICS_EVENTS.AUTH_ERROR, {
    method,
    error_type: overrides.type ?? mapped.type,
    error_message: (overrides.message ?? mapped.message).slice(0, 160),
    time_taken: 0,
    email_provided: false,
    timestamp: Date.now(),
  });
};

/**
 * Handles Firebase Auth email action links:
 * - Email verification
 * - Password reset
 * - Email change verification
 */
const EmailVerificationHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUserData } = useEnhancedAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [actionType, setActionType] = useState<string>('');

  const handleEmailVerification = useCallback(async (oobCode: string) => {
    try {
      await applyActionCode(auth, oobCode);
      await refreshUserData();
      setStatus('success');
      setMessage('Email verified successfully! You can now receive important updates.');
      if (typeof gtag !== 'undefined') {
        gtag('event', 'email_verified', { method: 'email_link' });
      }
      setTimeout(() => { navigate('/dashboard'); }, 3000);
    } catch (error: unknown) {
      if (auth.currentUser?.emailVerified) {
        setStatus('success');
        setMessage('Your email is already verified! You can now receive important updates.');
        setTimeout(() => { navigate('/dashboard'); }, 3000);
        return;
      }
      const firebaseError = error as { code?: string };
      if (firebaseError.code === 'auth/invalid-action-code' || firebaseError.code === 'auth/expired-action-code') {
        trackAuthError('verify_email', error, { type: 'stale_action_code' });
        setStatus('error');
        setMessage('This verification link has expired or already been used. Please request a new verification email.');
      } else {
        throw error;
      }
    }
  }, [refreshUserData, navigate]);

  const handlePasswordReset = useCallback(async (oobCode: string) => {
    const email = await verifyPasswordResetCode(auth, oobCode);
    sessionStorage.setItem('passwordResetCode', oobCode);
    sessionStorage.setItem('passwordResetEmail', email);
    setStatus('success');
    setMessage(`Password reset verified for ${email}. Redirecting to set new password...`);
    setTimeout(() => { navigate(`/finish-signin?mode=resetPassword&oobCode=${oobCode}`); }, 2000);
  }, [navigate]);

  const handleEmailRecovery = useCallback(async (oobCode: string) => {
    await applyActionCode(auth, oobCode);
    setStatus('success');
    setMessage('Email recovery completed successfully.');
    setTimeout(() => { navigate('/dashboard'); }, 3000);
  }, [navigate]);

  const handleSignInLink = useCallback((continueUrl: string | null) => {
    const params = new URLSearchParams(location.search);
    const destFromContinue = continueUrl
      ? new URLSearchParams(new URL(continueUrl).search).get('dest')
      : null;
    if (destFromContinue) {
      params.set('dest', destFromContinue);
    }
    navigate(`/finish-signin?${params.toString()}`, { replace: true });
  }, [location.search, navigate]);

  const handleEmailAction = useCallback(async (mode: string, oobCode: string, continueUrl: string | null) => {
    setActionType(mode);
    try {
      switch (mode) {
        case 'verifyEmail':
        case 'action':
          await handleEmailVerification(oobCode);
          break;
        case 'resetPassword':
          await handlePasswordReset(oobCode);
          break;
        case 'recoverEmail':
          await handleEmailRecovery(oobCode);
          break;
        case 'signIn':
          handleSignInLink(continueUrl);
          break;
        default:
          throw new Error('Unknown action mode');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('EmailVerificationHandler', 'Email action failed', { mode, error: errorMessage });
      trackAuthError(`email_action_${mode}`, error);
      setStatus('error');
      setMessage(errorMessage || 'Email verification failed.');
    }
  }, [handleEmailVerification, handlePasswordReset, handleEmailRecovery, handleSignInLink]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const mode = urlParams.get('mode');
    const oobCode = urlParams.get('oobCode');
    const continueUrl = urlParams.get('continueUrl');
    if (!mode || !oobCode) {
      // A link that arrives with no mode/oobCode means the params were stripped
      // in transit (mail-client rewriting, a redirect that dropped the query).
      // That is a delivery bug we could not see before.
      trackAuthError('email_action_missing_params', null, {
        type: 'missing_action_params',
        message: 'Invalid email verification link.',
      });
      setStatus('error');
      setMessage('Invalid email verification link.');
      return;
    }
    handleEmailAction(mode, oobCode, continueUrl);
  }, [location.search, handleEmailAction]);

  const getActionTitle = () => {
    switch (actionType) {
      case 'verifyEmail':
        return 'Verifying Your Email';
      case 'resetPassword':
        return 'Verifying Password Reset';
      case 'recoverEmail':
        return 'Recovering Your Email';
      default:
        return 'Processing Email Action';
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-neutral-100 rounded-lg shadow-ledge-lg p-6">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <LoadingSpinner />
              <h2 className="mt-4 text-xl font-semibold text-neutral-900">{getActionTitle()}</h2>
              <p className="mt-2 text-neutral-600">Please wait while we process your request...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="text-xl font-semibold text-success-600">Success!</h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              {actionType === 'verifyEmail' && (
                <div className="mt-4 p-4 bg-primary-50 rounded-lg">
                  <p className="text-sm text-primary-700">
                    You'll now receive study reminders and progress updates to help you succeed on your LCSW exam.
                  </p>
                </div>
              )}
            </>
          )}

          {status === 'error' && (
            <>
              <h2 className="text-xl font-semibold text-error-600">Verification Failed</h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              <div className="mt-6 space-y-2">
                <button onClick={() => navigate('/dashboard')} className="w-full px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700">
                  Go to Dashboard
                </button>
                <button onClick={() => navigate('/settings')} className="w-full px-4 py-2 bg-neutral-200 text-neutral-700 rounded hover:bg-neutral-300">
                  Account Settings
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationHandler;

